import UIRemoteApi from '../../../../remote-api/UIRemoteApi'
import { CustomMetric, customMetricSelectorInitialState, CustomMetricsState, LocalStorageCustomMetricSelector } from '../model'

export class CustomMetricsService {

  private readonly remoteAPI: UIRemoteApi

  constructor(remoteAPI: UIRemoteApi) {
    this.remoteAPI = remoteAPI
  }

  public fetchCustomMetrics(orgId: string, envId: string, appId: string, from?: string, to?: string): Promise<string[]> {
    return this.remoteAPI.fetchCustomMetrics(orgId, envId, appId, from, to)
  }

  public static getCustomMetricsAggregations(): string[] {
    return [
      'None',
      'count',
      'distinct',
      'integral',
      'mean',
      'median',
      'mode',
      'sum'
    ]
  }

  public fetchCustomMetricFieldKeys(orgId: string, envId: string, appId: string, metric: string): Promise<string[]> {
    return this.remoteAPI.fetchCustomMetricFieldKeys(orgId, envId, appId, metric)
  }

  public fetchCustomMetricDimensions(orgId: string, envId: string, appId: string, metric: string, from?: string, to?: string): Promise<string[]> {
    return this.remoteAPI.fetchCustomMetricDimensions(orgId, envId, appId, metric, from, to)
  }

  public static mergeCustomMetricsList(newMetrics: string[], previousMetrics?: CustomMetric[], appId?: string): CustomMetric[] {
    return newMetrics.map(metric => this.mergeCustomMetric(metric, previousMetrics, appId))
  }

  public static mergeCustomMetric(metricName: string, customMetricList?: CustomMetric[], appId?: string) {
    if (customMetricList) {
      const customMetric = customMetricList.find(metric => metric.name === metricName)
      if (customMetric) {
        return this.mergeCustomMetricWithLocalStorage(customMetric, appId || '')
      }
    }
    
    return this.mergeCustomMetricWithLocalStorage({name: metricName, selector: {...customMetricSelectorInitialState}}, appId || '')
  }

  public static mergeCustomMetricWithLocalStorage(customMetric: CustomMetric, appId: string): CustomMetric {
    const eventualMetric = localStorage.getItem(`${appId}-${customMetric.name}`)
    if (eventualMetric === null) {
      return customMetric;
    } else {
      const metricSelector: LocalStorageCustomMetricSelector = JSON.parse(eventualMetric)
      return {
        ...customMetric,
        selector: {
          aggregationSelector: {
            ...customMetric.selector.aggregationSelector,
            selected: metricSelector.aggregation
          },
          measureSelector: {
            ...customMetric.selector.measureSelector,
            selected: metricSelector.measure
          },
          dimensionSelector: {
            ...customMetric.selector.dimensionSelector,
            selected: metricSelector.dimension
          },
        }
      }
    }
  }

  public static aggregationsLoading(state: CustomMetricsState, metricName: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        metric.selector = {
          ...metric.selector,
          aggregationSelector: {
            ...metric.selector.aggregationSelector,
            aggregations: undefined,
            isLoading: true
          }
        }
      }
    }

    return newState
  }

  public static fetchedAggregations(state: CustomMetricsState, metricName: string, aggregations: string[], appId: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      const metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        const aggregation = metric.selector.aggregationSelector.selected
        metric.selector = {
          ...metric.selector,
          aggregationSelector: {
            aggregations: aggregations,
            selected: (aggregations.length > 0 && aggregation) ? this.getAggregationFromLocalStorage(metric.name, appId, aggregation) :
              this.getAggregationFromLocalStorage(metric.name, appId, undefined),
            isLoading: false
          }
        }
      }
    }

    return newState
  }

  public static measuresLoading(state: CustomMetricsState, metricName: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        metric.selector = {
          ...metric.selector,
          measureSelector: {
            ...metric.selector.measureSelector,
            measures: undefined,
            isLoading: true
          }
        }
      }
    }

    return newState
  }

  public static fetchedMeasures(state: CustomMetricsState, metricName: string, measures: string[], appId: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        const measure = metric.selector.measureSelector.selected
        metric.selector = {
          ...metric.selector,
          measureSelector: {
            measures: measures,
            selected: (measures.length > 0 && measure) ? this.getMeasureFromLocalStorage(metric.name, appId, measure) :
              this.getMeasureFromLocalStorage(metric.name, appId, undefined),
            isLoading: false
          }
        }
      }
    }

    return newState
  }

  public static dimensionsLoading(state: CustomMetricsState, metricName: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        metric.selector = {
          ...metric.selector,
          dimensionSelector: {
            ...metric.selector.dimensionSelector,
            dimensions: undefined,
            isLoading: true
          }
        }
      }
    }

    return newState
  }

  public static fetchedDimensions(state: CustomMetricsState, metricName: string, dimensions: string[], appId: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        const dimension = metric.selector.dimensionSelector.selected
        metric.selector = {
          ...metric.selector,
          dimensionSelector: {
            dimensions: dimensions,
            selected: (dimensions.length > 0 && dimension) ? this.getDimensionFromLocalStorage(metric.name, appId, dimension) :
              this.getDimensionFromLocalStorage(metric.name, appId, undefined),
            isLoading: false
          }
        }
      }
    }

    return newState
  }

  public static selectAggregationInState(state: CustomMetricsState, value: string, metricName: string): CustomMetric[] | undefined {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        metric.selector = {
          ...metric.selector,
          aggregationSelector: {
            ...metric.selector.aggregationSelector,
            selected: value
          }
        }
      }
    }

    return newState.metricList.metrics
  }

  public static selectMeasureInState(state: CustomMetricsState, value: string, metricName: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        metric.selector = {
          ...metric.selector,
          measureSelector: {
            ...metric.selector.measureSelector,
            selected: value
          }
        }
      }
    }

    return newState
  }

  public static selectDimensionInState(state: CustomMetricsState, value: string, metricName: string): CustomMetricsState {
    let newState: CustomMetricsState = {...state}
    if (newState.metricList.metrics) {
      let metric = newState.metricList.metrics.find(metric => metric.name === metricName)
      if (metric) {
        metric.selector = {
          ...metric.selector,
          dimensionSelector: {
            ...metric.selector.dimensionSelector,
            selected: value
          }
        }
      }
    }

    return newState
  }

  public static saveAggregationToLocalStorage(appId: string, metricName: string, aggregation: string) {
    const eventualMetric = localStorage.getItem(`${appId}-${metricName}`)
    if (eventualMetric !== null) {
      const metricSelector: LocalStorageCustomMetricSelector = JSON.parse(eventualMetric)
      localStorage.setItem(`${appId}-${metricName}`, JSON.stringify({
        ...metricSelector,
        aggregation: aggregation
      }))
    } else {
      localStorage.setItem(`${appId}-${metricName}`, JSON.stringify({
        aggregation: aggregation,
        measure: undefined,
        dimension: undefined
      }))
    }
  }

  public static saveMeasureToLocalStorage(appId: string, metricName: string, measure: string) {
    const eventualMetric = localStorage.getItem(`${appId}-${metricName}`)
    if (eventualMetric !== null) {
      const metricSelector: LocalStorageCustomMetricSelector = JSON.parse(eventualMetric)
      localStorage.setItem(`${appId}-${metricName}`, JSON.stringify({
        ...metricSelector,
        measure: measure
      }))
    } else {
      localStorage.setItem(`${appId}-${metricName}`, JSON.stringify({
        aggregation: undefined,
        measure: measure,
        dimension: undefined
      }))
    }
  }

  public static saveDimensionToLocalStorage(appId: string, metricName: string, dimension: string) {
    const eventualMetric = localStorage.getItem(`${appId}-${metricName}`)
    if (eventualMetric !== null) {
      const metricSelector: LocalStorageCustomMetricSelector = JSON.parse(eventualMetric)
      localStorage.setItem(`${appId}-${metricName}`, JSON.stringify({
        ...metricSelector,
        dimension: dimension
      }))
    } else {
      localStorage.setItem(`${appId}-${metricName}`, JSON.stringify({
        aggregation: undefined,
        measure: undefined,
        dimension: dimension
      }))
    }
  }

  public static getAggregationFromLocalStorage(metricName: string, appId: string, value: string | undefined) {
    const eventualMetric = localStorage.getItem(`${appId}-${metricName}`)
    if (eventualMetric !== null) {
      const metricSelector: LocalStorageCustomMetricSelector = JSON.parse(eventualMetric)
      return metricSelector.aggregation
    } else {
      return value
    }
  }

  public static getMeasureFromLocalStorage(metricName: string, appId: string, value: string | undefined) {
    const eventualMetric = localStorage.getItem(`${appId}-${metricName}`)
    if (eventualMetric !== null) {
      const metricSelector: LocalStorageCustomMetricSelector = JSON.parse(eventualMetric)
      return metricSelector.measure
    } else {
      return value
    }
  }

  public static getDimensionFromLocalStorage(metricName: string, appId: string, value: string | undefined) {
    const eventualMetric = localStorage.getItem(`${appId}-${metricName}`)
    if (eventualMetric !== null) {
      const metricSelector: LocalStorageCustomMetricSelector = JSON.parse(eventualMetric)
      return metricSelector.dimension
    } else {
      return value
    }
  }

}